import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { XMarkIcon, DocumentChartBarIcon, PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { chartOfAccountsService } from '../../../services/accounting/chart-of-accounts';
import { formatCurrency, formatDate } from '../../../utils/formatting';

/**
 * TrialBalanceModal Component
 * 
 * Modal for displaying and exporting trial balance report
 */
const TrialBalanceModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [trialBalance, setTrialBalance] = useState(null);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  // Load trial balance when modal opens
  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      loadTrialBalance();
    }
  }, [isOpen, currentCompany?.id, asOfDate]);

  const loadTrialBalance = async () => {
    setLoading(true);
    try {
      const trialBalanceData = await chartOfAccountsService.getTrialBalance(
        currentCompany.id,
        asOfDate ? new Date(asOfDate) : null
      );
      setTrialBalance(trialBalanceData);
    } catch (error) {
      console.error('Error loading trial balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!trialBalance) return;

    // Create CSV content
    const csvContent = [
      ['Account Code', 'Account Name', 'Debit Balance', 'Credit Balance'],
      ...trialBalance.accounts.map(account => [
        account.accountCode,
        account.accountName,
        account.debitBalance || 0,
        account.creditBalance || 0
      ]),
      ['', 'TOTALS', trialBalance.totalDebits, trialBalance.totalCredits]
    ].map(row => row.join(',')).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trial-balance-${asOfDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <DocumentChartBarIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('coa.trial_balance', 'Trial Balance')}
              </h2>
              <p className="text-sm text-gray-500">
                {currentCompany?.companyName || t('coa.company_name', 'Company Name')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="btn btn-outline btn-sm"
              disabled={loading || !trialBalance}
            >
              <PrinterIcon className="w-4 h-4 mr-2" />
              {t('common.print', 'Print')}
            </button>
            <button
              onClick={handleExport}
              className="btn btn-outline btn-sm"
              disabled={loading || !trialBalance}
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              {t('common.export', 'Export')}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Date Selection */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('coa.as_of_date', 'As of Date')}:
            </label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="input input-bordered input-sm"
            />
            <button
              onClick={loadTrialBalance}
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                t('common.refresh', 'Refresh')
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600">{t('common.loading', t('common.loading'))}</span>
            </div>
          ) : trialBalance ? (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="text-center border-b border-gray-200 pb-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  {currentCompany?.companyName || t('coa.company_name', 'Company Name')}
                </h1>
                <h2 className="text-xl font-semibold text-gray-700 mt-2">
                  {t('coa.trial_balance', 'Trial Balance')}
                </h2>
                <p className="text-gray-600 mt-1">
                  {t('coa.as_of', 'As of')} {formatDate(asOfDate)}
                </p>
              </div>

              {/* Balance Status */}
              <div className="flex items-center justify-center">
                <div className={`px-4 py-2 rounded-lg ${
                  trialBalance.isBalanced 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {trialBalance.isBalanced 
                    ? t('coa.trial_balance_balanced', 'Trial Balance is Balanced') 
                    : t('coa.trial_balance_unbalanced', 'Trial Balance is NOT Balanced')
                  }
                </div>
              </div>

              {/* Trial Balance Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('coa.account_code', 'Code')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('coa.account_name', 'Account Name')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('coa.debit_balance', 'Debit Balance')}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('coa.credit_balance', 'Credit Balance')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trialBalance.accounts.map((account) => (
                      <tr key={account.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {account.accountCode}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div>
                            <div className="font-medium">{account.accountName}</div>
                            {account.accountNameAr && (
                              <div className="text-gray-600 text-xs">{account.accountNameAr}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {account.debitBalance > 0 ? (
                            <span className="font-medium text-gray-900">
                              {formatCurrency(account.debitBalance)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          {account.creditBalance > 0 ? (
                            <span className="font-medium text-gray-900">
                              {formatCurrency(account.creditBalance)}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="2" className="px-6 py-4 text-sm font-bold text-gray-900">
                        {t('coa.totals', 'TOTALS')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                        {formatCurrency(trialBalance.totalDebits)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                        {formatCurrency(trialBalance.totalCredits)}
                      </td>
                    </tr>
                    {!trialBalance.isBalanced && (
                      <tr>
                        <td colSpan="2" className="px-6 py-2 text-sm font-medium text-red-600">
                          {t('coa.difference', 'Difference')}
                        </td>
                        <td colSpan="2" className="px-6 py-2 text-sm text-right font-medium text-red-600">
                          {formatCurrency(Math.abs(trialBalance.totalDebits - trialBalance.totalCredits))}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {trialBalance.accounts.length}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('coa.accounts_with_balance', 'Accounts with Balance')}
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(trialBalance.totalDebits)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('coa.total_debits', 'Total Debits')}
                    </div>
                  </div>
                </div>
                
                <div className="card">
                  <div className="card-body text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {formatCurrency(trialBalance.totalCredits)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('coa.total_credits', 'Total Credits')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Report Footer */}
              <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-4">
                <p>
                  {t('coa.generated_on', 'Generated on')} {formatDate(new Date())} 
                  {' '}{t('coa.by', 'by')} {currentCompany?.companyName || t('coa.system', 'System')}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <DocumentChartBarIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">
                {t('coa.no_trial_balance_data', 'No trial balance data available')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0 bg-white">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            {t('common.close', t('common.close'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBalanceModal;